'use client'
import { useParams } from 'next/navigation'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import styles from './FooterInterlinks.module.scss'
import type { TopBrand, TopCountryFilter } from '@/country/types'
import { getCountryContent } from '@/country/use-cases'
import { getConfig } from '@/tools/config'
import type { Language } from '@/tools/i18n'
import { useLocalisation } from '@/tools/i18n/client'
import { useErrorHandling } from '@/tools/o11y/client'
import { slugify } from '@/tools/strings/slugify'
import { FooterLink } from '@/tools/ui/components/server/footer/ui/components/footer-link/FooterLink'

type Props = {
  shouldShowInterlinks?: boolean
}
export const FooterInterlinks: FC<Props> = ({ shouldShowInterlinks }) => {
  const [topCategories, setTopCategories] = useState<TopCountryFilter[]>([
    { filterName: '', filterSlug: '' },
  ])
  const [popularBrands, setPopularBrands] = useState<TopBrand[]>([])

  const { country, language } = useParams<{ country?: string; language?: Language }>()
  const { execWithErrorTracking } = useErrorHandling()
  const { appUrl } = getConfig()
  const { t } = useLocalisation()

  useEffect(() => {
    execWithErrorTracking(async () => {
      if (country && language) {
        const countryContent = await getCountryContent({ country, language })
        setTopCategories(countryContent.topFilters)
        setPopularBrands(countryContent.topBrands)
      }
    })
  }, [execWithErrorTracking, country, language])

  const buildLink = (slug?: TopCountryFilter | TopBrand) => {
    if (slug && 'filterSlug' in slug) {
      return `${appUrl}/${country}/${language}/${slugify(t('seo-landing.breadcrumbs.delivery'))}_glovo/${slug.filterSlug}`
    }
    if (slug && 'brandSlug' in slug) {
      return `${appUrl}/${country}/${language}/glovo_${slugify(t('seo-landing.breadcrumbs.delivery'))}/${slug.brandSlug}`
    }
    return `${appUrl}/${country}/${language}/${slugify(t('seo-landing.breadcrumbs.delivery'))}_glovo/`
  }

  const localizedCountryName = country ? t(`common.country.${country.toUpperCase()}`) : ''

  return (
    country &&
    shouldShowInterlinks && (
      <div className={styles.wrapper}>
        {popularBrands.length > 0 && (
          <section className={styles.popularBrands}>
            <h3 className={styles.title}>
              {`${t('seo-landing.top-brands.title')} : ${localizedCountryName}`}
            </h3>
            <div className={styles.links}>
              {popularBrands.map((brand) => (
                <FooterLink
                  key={brand.brandName}
                  item={{ href: buildLink(brand), label: brand.brandName, type: 'link' }}
                />
              ))}
            </div>
          </section>
        )}
        {topCategories.length > 0 && (
          <section className={styles.topCategories}>
            <h3 className={styles.title}>
              {`${t('seo-landing.top-filters.title')} : ${localizedCountryName}`}
            </h3>
            <div className={styles.links}>
              {topCategories.map((filter) => (
                <FooterLink
                  key={filter.filterName}
                  item={{ href: buildLink(filter), label: filter.filterName, type: 'link' }}
                />
              ))}
            </div>
            <FooterLink
              item={{
                href: buildLink(),
                label: `${t('seo-landing.top-filters.all-filters')}`,
                type: 'link',
              }}
            />
          </section>
        )}
      </div>
    )
  )
}
